<template>
	<div class="slider">
		<!-- All the image to display -->
		<div class="slider__overlay" />
		<div
			v-for="(item, index) in items"
			:key="index"
			ref="images"
			class="slider__image"
			data-cy="slider-image"
			:style="{
				'background-image': 'url(' + item.image + ')'
			}"
		/>

		<!-- Text to display -->
		<div
			v-for="item in items"
			v-show="$scopedSlots.content || (item.title && item.title.length > 0)"
			:key="`text-${item.id}`"
			ref="texts"
			class="slider__content"
		>
			<div
				v-if="item && item.title"
				class="slider__title"
			>
				{{ item.title }}
			</div>

			<div
				v-if="item && item.subtitle"
				class="slider__subtitle"
			>
				{{ item.subtitle }}
			</div>

			<slot
				name="content"
				:slide="item"
			/>

			<helio-button
				v-if="hasButton(item)"
				class="slider__button"
				size="large"
				:color="item.button.color"
				:text="item.button.text"
				:link="item.button.link"
				:target="item.button.target ? item.button.target : '_self'"
			/>
		</div>

		<!-- Progress Bar -->
		<div
			v-if="showNavigation"
			class="slider__progress-bar-container"
			data-cy="progress-bar-container"
		>
			<div
				v-for="item in items"
				:key="`button-${item.id}`"
				class="slider__progress-bar"
				data-cy="progress-bar"
				@click="switchToSlide(item.id)"
			>
				<helio-progress-bar
					color="info"
					:total="100"
					:value="getSlideValue(item.id)"
					:duration="getSlideDuration(item.id)"
					:width="39"
					:height="3"
				/>
			</div>
		</div>
	</div>
</template>
<script>
import ProgressBar from '../progress-bar/ProgressBar.vue';
import Button from '../button/Button.vue';

const FRAME_INTERVAL = 90;

export default {
	name: 'Slider',

	components: {
		'helio-button': Button,
		'helio-progress-bar': ProgressBar,
	},

	props: {
		showNavigation: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the progress bar navigation buttons will be shown',
		},

		slides: {
			type: Array,
			required: true,
			validator: slides => slides.length >= 1,
			description: 'Supplies information about each slide\'s contents to the Slider component',
		},

		speed: {
			type: Number,
			required: false,
			default: 5000,
			validator: value => value >= 100,
			description: 'Sets the frequency with which the slides change',
		},
	},

	slots: {
		content: {
			required: true,
			scoped: true,
			description: 'Supplies the Slider component with custom content, which will overwrite the default content format',
		},
	},

	data() {
		return {
			mainIntervalId: -1,
			show: true,
			selectedIndex: null,
			currentFrame: 0,
			items: [],
		};
	},

	watch: {
		slides() {
			this.generateSlides();
		},
	},

	mounted() {
		const { images, texts } = this.$refs;

		for (let i = 1; i < images.length; i += 1) {
			const element = images[i];
			const text = texts[i];
			element.style.visibility = 'hidden';
			text.style.visibility = 'hidden';
		}

		texts[0].style.opacity = '1';

		// Starts the slider after the component is mounted
		setTimeout(() => {
			this.switchToSlide(0);
		}, 10);
	},

	created() {
		this.generateSlides();
	},

	beforeDestroy() {
		clearInterval(this.mainIntervalId);
	},

	methods: {
		generateSlides() {
			// Extract the valid items from the slides
			let nb = 0;
			this.items = [];
			this.slides.forEach((slide) => {
				if (slide.image == null) {
					throw new Error(`Wrong slide parameter: Slide must have property. Object ${slide}`);
				}

				// We create the basic slide object
				let formattedSlide = {
					id: nb,
					title: slide.title || '',
					subtitle: slide.subtitle || '',
					button: slide.button,
					image: slide.image,
					progress: 5,
				};

				// If there's additional properties to be added to the slide, they will.
				// The basic slide properties won't be overriden.
				formattedSlide = { ...slide, ...formattedSlide };

				this.items.push(formattedSlide);

				nb += 1;
			});
		},

		switchToSlide(index) {
			clearInterval(this.mainIntervalId);
			this.nextItem(index);
			this.currentFrame = 0;
			this.mainIntervalId = setInterval(this.frame, FRAME_INTERVAL);
		},

		frame() {
			this.currentFrame += FRAME_INTERVAL;
			if (this.currentFrame >= this.speed) {
				this.currentFrame = 0;
				this.nextItem();
			}
		},

		hasButton(item) {
			return item.button
				&& item.button.text != null
				&& item.button.text.length > 0
				&& item.button.link != null
				&& item.button.link.length > 0;
		},

		nextItem(nextSlideIndex) {
			const { images, texts } = this.$refs;

			if (this.selectedIndex != null) {
				texts[0].style.opacity = '0';
				images[this.selectedIndex].classList.add('slider__image--hide');
				texts[this.selectedIndex].classList.add('slider__content--hide');
				images[this.selectedIndex].classList.remove('slider__image--show');
				texts[this.selectedIndex].classList.remove('slider__content--show');
			}

			let nextIndex;
			if (nextSlideIndex != null) {
				nextIndex = nextSlideIndex;
			} else if (this.selectedIndex != null) {
				nextIndex = (this.selectedIndex + 1 < this.items.length ? this.selectedIndex + 1 : 0);
			}

			images[nextIndex].style.visibility = 'visible';
			texts[nextIndex].style.visibility = 'visible';
			images[nextIndex].classList.add('slider__image--show');
			texts[nextIndex].classList.add('slider__content--show');
			this.selectedIndex = nextIndex;

			this.$emit('changed');
		},

		getSlideValue(id) {
			return id === this.selectedIndex ? 100 : 0;
		},

		getSlideDuration(id) {
			return id === this.selectedIndex ? this.speed : 0;
		},
	},
};
</script>

<style lang="scss" src="./slider.scss" />
